import { Fragment } from "react";
import { type ReactNode, useState } from "react";
import { LuQuote, LuRepeat } from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { focusIOSKeyboardBridge } from "@/components/app/editor-v3/editor-dom-focus";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import type { PostType } from "@/contexts/feed-context";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { useSharePost } from "@/hooks-api/use-share-post";
import { cn } from "@/utils/lib";

import { HidePostAction } from "../app/post/post-actions/hide-post-action";
import { Spinner } from "./spinner";

export const RepostDropdown = ({
  children,
  className,
  post,
  updatePost,
  triggerClassName = "text-base",
}: {
  children: ReactNode;
  className?: string;
  post: PostType;
  triggerClassName?: string;
  updatePost?: (hash: string, updatedPost: PostType) => void;
}) => {
  const [loading, setLoading] = useState(false);

  if (!post.postHash) {
    throw new Error("Post hash is required");
  }

  const { hasCurrentPostRepostedByCurrentUser, currentUserRepost } =
    useGetCurrentUserPostStats(post);

  const { onShare } = useSharePost(post.postHash);
  const [sharing, setSharing] = useState(false);

  const onShareClick = async () => {
    setSharing(true);
    await onShare();
    updatePost?.(post.postHash!, {
      ...post,
      postStats: {
        ...post.postStats,
        repostCount: (post.postStats?.repostCount || 0) + 1,
      },
    });
    setSharing(false);
  };

  return (
    <div className="flex">
      <DropdownDrawer historyName="repost-actions" title="Repost actions">
        <DropdownDrawerTrigger
          render={
            <div
              className={cn(
                `
                  flex cursor-pointer items-center gap-2 text-muted
                  hover:text-primary
                `,
                {
                  "text-primary!": hasCurrentPostRepostedByCurrentUser,
                },
                triggerClassName,
                className,
              )}
            >
              {sharing ? (
                <Spinner size={13} />
              ) : (
                <Fragment key="repost">
                  <LuRepeat />
                  {children}
                </Fragment>
              )}
            </div>
          }
        />
        <DropdownDrawerContent>
          <DropdownDrawerItem
            onClick={onShareClick}
            disabled={hasCurrentPostRepostedByCurrentUser}
          >
            <LuRepeat className={`mr-2 size-4`} />
            <span>
              {hasCurrentPostRepostedByCurrentUser ? "Reposted" : "Repost"}
            </span>
          </DropdownDrawerItem>
          {hasCurrentPostRepostedByCurrentUser && currentUserRepost && (
            <DropdownDrawerItem
              disabled={loading}
              render={
                <HidePostAction
                  {...{
                    updatePost,
                    post: currentUserRepost,
                    setLoading,
                    repost: true,
                  }}
                />
              }
            />
          )}
          <DropdownDrawerItem
            onClick={() => {
              focusIOSKeyboardBridge();
              setDialog(DIALOG.QuotePost, {
                open: true,
                replace: true,
                params: {
                  postHash: post.postHash!,
                },
                props: {
                  post,
                  refetchPostStats: () => {
                    updatePost?.(post.postHash!, {
                      ...post,
                      postStats: {
                        ...post.postStats,
                        quoteCount: (post.postStats?.quoteCount || 0) + 1,
                      },
                    });
                  },
                },
              });
            }}
          >
            <LuQuote className="mr-2 size-4" />
            <span>Quote Post</span>
          </DropdownDrawerItem>
        </DropdownDrawerContent>
      </DropdownDrawer>
    </div>
  );
};
